/**
 * API Access Settings - Token generation and MCP config UI
 * Provides UI for generating Cursor Personal Access Tokens and MCP config snippets
 */

import { useState, useEffect, useCallback } from 'react'
import { Key, Eye, EyeOff, Trash2, Copy, Check, Loader2, RefreshCw, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  SettingsSection,
  SettingsSectionRow,
} from '@/components/features/settings/settings-section'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { testIds } from '@/lib/test-ids'
import { createCursorToken, listCursorTokens, revokeCursorToken, type CursorToken } from '@/lib/api/tokens'
import { API_BASE_URL } from '@/lib/api/core'

/** API Access settings component for token generation and MCP configuration */
export function APIAccessSettings() {
  const [tokens, setTokens] = useState<CursorToken[]>([])
  const [loading, setLoading] = useState(true)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [tokenToRevoke, setTokenToRevoke] = useState<CursorToken | null>(null)
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [tokenName, setTokenName] = useState('')
  const [showTokenValue, setShowTokenValue] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [copiedToken, setCopiedToken] = useState(false)
  const [copiedConfig, setCopiedConfig] = useState(false)

  /** Fetch and display all tokens for the current user */
  const fetchTokens = useCallback(async () => {
    try {
      const data = await listCursorTokens()
      setTokens(data)
    } catch (err) {
      console.error('Failed to load tokens:', err)
      toast.error('Failed to load access tokens')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTokens()
  }, [fetchTokens])

  /** Generate a new access token with the provided name */
  const handleGenerate = async () => {
    if (!tokenName.trim()) {
      toast.error('Please enter a token name')
      return
    }

    setGenerating(true)
    try {
      const result = await createCursorToken({ name: tokenName.trim() })
      setGeneratedToken(result.token)
      setTokenName('')
      toast.success('Access token generated')
    } catch (err) {
      console.error('Failed to generate token:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to generate token')
    } finally {
      setGenerating(false)
    }
  }

  /** Close the generate dialog and refresh the token list */
  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false)
    setGeneratedToken(null)
    setTokenName('')
    setShowTokenValue(false)
    fetchTokens()
  }

  /** Revoke the selected token and refresh the list */
  const handleRevoke = async () => {
    if (!tokenToRevoke) return

    setRevoking(true)
    try {
      await revokeCursorToken(tokenToRevoke.id)
      toast.success(`Token "${tokenToRevoke.name}" revoked`)
      setRevokeDialogOpen(false)
      setTokenToRevoke(null)
      fetchTokens()
    } catch (err) {
      console.error('Failed to revoke token:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to revoke token')
    } finally {
      setRevoking(false)
    }
  }

  /** Copy text to clipboard and show feedback */
  const copyToClipboard = async (text: string, type: 'token' | 'config') => {
    try {
      await navigator.clipboard.writeText(text)
      if (type === 'token') {
        setCopiedToken(true)
        setTimeout(() => setCopiedToken(false), 2000)
      } else {
        setCopiedConfig(true)
        setTimeout(() => setCopiedConfig(false), 2000)
      }
      toast.success('Copied to clipboard')
    } catch (err) {
      console.error('Failed to copy:', err)
      toast.error('Failed to copy to clipboard')
    }
  }

  /** Generate the MCP configuration JSON for Cursor with the provided token */
  const getMcpConfig = (token: string) => {
    return JSON.stringify({
      flownotes: {
        command: 'npx',
        args: ['-y', '@flownotes/mcp'],
        env: {
          FLOWNOTES_API_URL: API_BASE_URL,
          FLOWNOTES_API_KEY: token,
        },
      },
    }, null, 2)
  }

  /** Return an expiry badge component based on days until expiration */
  const getExpiryBadge = (expiresAt: string | null) => {
    if (!expiresAt) return null

    const expiryDate = new Date(expiresAt)
    const now = new Date()
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (daysUntilExpiry < 0) {
      return (
        <Badge variant="outline" className="text-red-600 border-red-500/40 bg-red-500/10 text-xs">
          Expired
        </Badge>
      )
    } else if (daysUntilExpiry <= 7) {
      return (
        <Badge variant="outline" className="text-yellow-600 border-yellow-500/40 bg-yellow-500/10 text-xs">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Expires soon
        </Badge>
      )
    }
    return null
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading access tokens...
      </div>
    )
  }

  return (
    <div data-testid={testIds.apiAccess.tab}>
      <SettingsSection
        icon={<Key />}
        title="API Access Tokens"
        description="Generate personal access tokens to connect Cursor or other tools to Maater."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={fetchTokens} title="Refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setGenerateDialogOpen(true)}
              data-testid={testIds.apiAccess.newTokenButton}
            >
              New access token
            </Button>
          </div>
        }
      >
        {tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No access tokens yet. Generate one to connect Cursor or other tools.
          </p>
        ) : (
          <div className="space-y-3" data-testid={testIds.apiAccess.tokenList}>
            {tokens.map((token) => {
              const expiryBadge = getExpiryBadge(token.expires_at)
              const lastUsed = token.last_used_at
                ? new Date(token.last_used_at).toLocaleDateString()
                : 'Never used'

              return (
                <SettingsSectionRow key={token.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{token.name}</span>
                      {expiryBadge}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => {
                        setTokenToRevoke(token)
                        setRevokeDialogOpen(true)
                      }}
                      data-testid={testIds.apiAccess.revokeButton}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Revoke
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium">Created:</span>{' '}
                      {token.created_at ? new Date(token.created_at).toLocaleDateString() : 'Unknown'}
                    </div>
                    <div>
                      <span className="font-medium">Last used:</span> {lastUsed}
                    </div>
                    <div>
                      <span className="font-medium">Expires:</span>{' '}
                      {token.expires_at ? new Date(token.expires_at).toLocaleDateString() : 'Never'}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">Scopes:</span> {token.scopes.join(', ')}
                  </div>
                </SettingsSectionRow>
              )
            })}
          </div>
        )}
      </SettingsSection>

      {/* Generate token dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid={testIds.apiAccess.generateDialog}>
          {!generatedToken ? (
            <>
              <DialogHeader>
                <DialogTitle>Generate new access token</DialogTitle>
                <DialogDescription>
                  Create a personal access token to connect Cursor or other tools to Maater.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Token name</label>
                  <Input
                    placeholder="e.g. My MacBook Pro"
                    value={tokenName}
                    onChange={(e) => setTokenName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                    maxLength={64}
                    autoFocus
                    data-testid={testIds.apiAccess.tokenNameInput}
                  />
                  <p className="text-xs text-muted-foreground">
                    A descriptive name to help you identify this token later.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Scopes</label>
                  <div className="text-sm text-muted-foreground">
                    <code className="text-xs bg-muted px-2 py-1 rounded">documents:sync</code>
                    {' '}
                    <code className="text-xs bg-muted px-2 py-1 rounded">documents:read</code>
                    {' '}
                    <code className="text-xs bg-muted px-2 py-1 rounded">documents:write</code>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    All tokens have full access to your documents. Granular scopes coming soon.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => setGenerateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={!tokenName.trim() || generating}
                  data-testid={testIds.apiAccess.generateButton}
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Generate
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Copy your access token</DialogTitle>
                <DialogDescription>
                  Save your token now. It will not be shown again.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Your token</label>
                  <div className="relative">
                    <Input
                      type={showTokenValue ? 'text' : 'password'}
                      value={generatedToken}
                      readOnly
                      className="pr-20 font-mono text-sm"
                      data-testid={testIds.apiAccess.tokenValue}
                    />
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setShowTokenValue(!showTokenValue)}
                        data-testid={testIds.apiAccess.tokenValueToggle}
                      >
                        {showTokenValue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(generatedToken, 'token')}
                        data-testid={testIds.apiAccess.copyToken}
                      >
                        {copiedToken ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Cursor MCP configuration</label>
                  <div className="relative">
                    <pre
                      className="text-xs bg-muted p-3 rounded-md overflow-x-auto font-mono whitespace-pre-wrap break-all"
                      data-testid={testIds.apiAccess.mcpConfig}
                    >
                      {getMcpConfig(generatedToken)}
                    </pre>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-2 h-7 w-7"
                      onClick={() => copyToClipboard(getMcpConfig(generatedToken), 'config')}
                      data-testid={testIds.apiAccess.copyConfig}
                    >
                      {copiedConfig ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Add this configuration to your Cursor settings to connect the Maater MCP.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Button
                  onClick={handleCloseGenerateDialog}
                  data-testid={testIds.apiAccess.doneButton}
                >
                  I've saved my token
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent data-testid={testIds.apiAccess.revokeConfirm}>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access token?</AlertDialogTitle>
            <AlertDialogDescription>
              {tokenToRevoke && (
                <>
                  Revoke <strong>{tokenToRevoke.name}</strong>? Any tools using this token will lose
                  access immediately. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={revoking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
